/** Angular */
import { Injectable, Injector } from '@angular/core';
/** Rxjs */
import { tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
import { BydAuthService, BydUserAccount } from '@sixense/byd-auth-service';
/** Functions */
import { BydGenerateParams } from '../../../functions/byd-generate-params.function';
/** Enums  */
import { BydUserServiceEnum } from '../enums/byd-user-service.enum';
/** Interfaces  */
import { DsUsersFiltersInterface } from '../interfaces/ds-users-filters.interface';
/** Models */
import { BydCurrentLanguage } from '../models/byd-current-language';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
import { DsUsersFiltersModel } from '../models/ds-users-filters.model';

@Injectable({
  providedIn: 'root',
})
export class DsUserService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'path' from BydRhsAbstractService
   */
  public path: string;

  /**
   * @param injector creates a service injector instance
   * @param bydAuthService the byd authentication service
   */
  constructor(protected injector: Injector, protected bydAuthService: BydAuthService) {
    super(injector);
  }

  /**
   * Get the current user details.
   *
   * @returns Observable type `<BydUserAccount>`
   */
  public getUser(): Observable<BydUserAccount> {
    this.path = BydUserServiceEnum.GET_USER_ENDPOINT;
    return this.getModel<BydUserAccount>().pipe(
      tap((response) => {
        if (response) {
          this.bydAuthService.currentAccount = response;
        }
      })
    );
  }

  /**
   * Check if the user login is already used.
   *
   * @param login Email user
   * @param id the user id
   * @param number [id] optional User id. If provided, search for used login except if it is the current user's login.
   * @returns Observable type `<boolean>`
   */
  public checkUserLogin(login: string, id?: number): Observable<boolean> {
    this.path = '/users/v2/checklogin';
    const body = {
      id,
      login,
    };
    return this.post<boolean>({ body });
  }

  /**
   * Get list of all users with pagination.
   *
   * @param elementsPerPage Max number of elements per page
   * @param pageIndex Page index to query
   * @param filters Filters on list
   * @returns Observable type `BydModel<BydUserAccount>>`
   */
  public getAllUsers(
    elementsPerPage = 20,
    pageIndex = 0,
    filters?: DsUsersFiltersModel
  ): Observable<BydModel<BydUserAccount>> {
    this.path = '/v2/users/list';
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams(filters);
    return this.getModels<BydUserAccount>({ headers, params });
  }

  /**
   * Get selected project users with pagination.
   *
   * @param projectId Project ID
   * @param elementsPerPage Max number of elements per page
   * @param pageIndex Page index to query
   * @param filters Filters on list
   * @returns Observable type `BydModel<BydUserAccount>>`
   */
  public getProjectUsers(
    projectId: number,
    elementsPerPage = 20,
    pageIndex = 0,
    filters?: DsUsersFiltersInterface
  ): Observable<BydModel<BydUserAccount>> {
    this.path = `/v3/projects/${projectId}/users`;
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(elementsPerPage, pageIndex);
    const params = BydGenerateParams<DsUsersFiltersInterface>(filters);
    return this.getModels<BydUserAccount>({ headers, params });
  }

  /**
   * Get user by login.
   *
   * @param  Login Email user
   * @returns Observable type `<BydUserAccount>`
   */
  public getUserByLogin(Login: string): Observable<BydUserAccount> {
    this.path = '/users/bylogin';
    const body = {
      login: Login,
    };
    return this.postModel<BydUserAccount>({ body });
  }

  /**
   * Patches the selected user's sxd_admin property to the desired value.
   *
   * @param userId Selected user id
   * @param value value to set for sxd admin
   * @returns Observable<boolean>
   */
  public patchSxdAdmin(userId: number, value: boolean): Observable<boolean> {
    this.path = `/users/${userId}`;
    const body = {
      sxd_admin: value,
    };
    return this.patch<boolean>({ body });
  }

  /**
   * Create a new user.
   *
   * @param user New user object
   * @returns Observable type `<BydUserAccount>`
   */
  public postUser(user: BydUserAccount): Observable<BydUserAccount> {
    this.path = '/users';
    return this.postModel<BydUserAccount>({ body: user });
  }

  /**
   * Update user.
   *
   * @param  user New user values
   * @returns Observable type `<BydUserAccount>`
   */
  public updateUser(user: BydUserAccount): Observable<BydUserAccount> {
    this.path = `/users/${user.account_id}`;
    return this.putModel<BydUserAccount>({ body: user });
  }

  /**
   * Get language of current user.
   *
   * @param userId current user id
   * @returns Observable<string>
   */
  public getLanguage(userId: number): Observable<BydCurrentLanguage> {
    this.path = `/users/${userId}/lang`;
    // Add responseType 'text', otherwhise we get an Http error
    return this.get<BydCurrentLanguage>();
  }

  /**
   * Update current user's language.
   *
   * @param userId current user id
   * @param language selected language
   * @returns Observable<boolean>
   */
  public patchLanguage(userId: number, language: string): Observable<boolean> {
    this.path = `/users/${userId}`;
    const body = {
      account_langue: language,
    };
    return this.patch<boolean>({ body });
  }

  /**
   * Create BydUserAccount instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `BydUserAccount` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): BydUserAccount {
    return new BydUserAccount(jsonObject);
  }
}
